import { getLocalTimeZone } from "@internationalized/date";
import { Suspense, useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";

import {
  Alert,
  Button,
  Card,
  DialogTrigger,
  Icon,
  Icons,
  LazyTabPanel,
  Loading,
  Modal,
  ModalOverlay,
  Tab,
  TabList,
  Tabs,
  View,
} from "@phoenix/components";
import type { APIKeyFormParams } from "@phoenix/components/auth";
import {
  CreateAPIKeyDialog,
  OneTimeAPIKeyDialog,
} from "@phoenix/components/auth";

import type { APIKeysCardCreateSystemAPIKeyMutation } from "./__generated__/APIKeysCardCreateSystemAPIKeyMutation.graphql";
import type { APIKeysCardQuery } from "./__generated__/APIKeysCardQuery.graphql";
import { SystemAPIKeysTable } from "./SystemAPIKeysTable";
import { UserAPIKeysTable } from "./UserAPIKeysTable";

import { useNotifyError } from "@phoenix/contexts";

function APIKeysCardContent({
  activeTab,
  onTabChange,
  fetchKey,
}: {
  activeTab: string;
  onTabChange: (id: string) => void;
  fetchKey: number;
}) {
  const query = useLazyLoadQuery<APIKeysCardQuery>(
    graphql`
      query APIKeysCardQuery {
        ...SystemAPIKeysTableFragment
        ...UserAPIKeysTableFragment
      }
    `,
    {},
    { fetchPolicy: "network-only", fetchKey }
  );

  return (
    <Tabs
      selectedKey={activeTab}
      onSelectionChange={(id) => onTabChange(id as string)}
    >
      <TabList>
        <Tab id="system">System Keys</Tab>
        <Tab id="user">User Keys</Tab>
      </TabList>
      <LazyTabPanel id="system">
        <SystemAPIKeysTable query={query} />
      </LazyTabPanel>
      <LazyTabPanel id="user">
        <UserAPIKeysTable query={query} />
      </LazyTabPanel>
    </Tabs>
  );
}

export function APIKeysCard() {
  const notifyError = useNotifyError();
  const [activeTab, setActiveTab] = useState("system");
  const [showCreateAPIKeyDialog, setShowCreateAPIKeyDialog] = useState(false);
  const [showOneTimeAPIKeyJwt, setShowOneTimeAPIKeyJwt] = useState<
    string | null
  >(null);
  const [fetchKey, setFetchKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [commitSystemKey, isCommittingSystemKey] =
    useMutation<APIKeysCardCreateSystemAPIKeyMutation>(graphql`
      mutation APIKeysCardCreateSystemAPIKeyMutation(
        $name: String!
        $description: String = null
        $expiresAt: DateTime = null
      ) {
        createSystemApiKey(
          input: { name: $name, description: $description, expiresAt: $expiresAt }
        ) {
          jwt
          query {
            ...SystemAPIKeysTableFragment
          }
          apiKey {
            id
          }
        }
      }
    `);

  const [commitUserKey, isCommittingUserKey] = useMutation(graphql`
    mutation APIKeysCardCreateUserAPIKeyMutation(
      $input: CreateUserApiKeyInput!
    ) {
      createUserApiKey(input: $input) {
        jwt
        query {
          ...UserAPIKeysTableFragment
        }
        apiKey {
          id
        }
      }
    }
  `);

  const isCommitting = isCommittingSystemKey || isCommittingUserKey;

  const onSubmit = useCallback(
    (data: APIKeyFormParams) => {
      const variables = {
        name: data.name,
        description: data.description,
        expiresAt:
          data.expiresAt?.toDate(getLocalTimeZone()).toISOString() || null,
      };

      if (activeTab === "system") {
        commitSystemKey({
          variables,
          onCompleted: (response: any) => {
            setFetchKey((prev) => prev + 1);
            setShowCreateAPIKeyDialog(false);
            setShowOneTimeAPIKeyJwt(response.createSystemApiKey.jwt);
          },
          onError: (error) => {
            notifyError({
              title: "Error creating system key",
              message: error.message,
            });
          },
        });
      } else {
        commitUserKey({
          variables: { input: variables },
          onCompleted: (response: any) => {
            setFetchKey((prev) => prev + 1);
            setShowCreateAPIKeyDialog(false);
            setShowOneTimeAPIKeyJwt(response.createUserApiKey.jwt);
          },
          onError: (error) => {
            notifyError({
              title: "Error creating user key",
              message: error.message,
            });
          },
        });
      }
    },
    [activeTab, commitSystemKey, commitUserKey, notifyError]
  );

  return (
    <div>
      <Card
        titleSeparator={false}
        title="API Keys"
        extra={
          <DialogTrigger
            isOpen={showCreateAPIKeyDialog}
            onOpenChange={(isOpen: boolean) => setShowCreateAPIKeyDialog(isOpen)}
          >
            <Button
              size="S"
              onPress={() => setShowCreateAPIKeyDialog(true)}
              leadingVisual={<Icon svg={<Icons.PlusCircleOutline />} />}
            >
              {activeTab === "system" ? "System Key" : "User Key"}
            </Button>
            <ModalOverlay>
              <Modal size="M">
                <CreateAPIKeyDialog
                  onSubmit={onSubmit}
                  isCommitting={isCommitting}
                  defaultName={activeTab === "system" ? "System" : "Personal"}
                />
              </Modal>
            </ModalOverlay>
          </DialogTrigger>
        }
      >
        {error && <Alert variant="danger">{error}</Alert>}
        <Suspense
          fallback={
            <View padding="size-100">
              <Loading />
            </View>
          }
        >
          <APIKeysCardContent
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id)}
            fetchKey={fetchKey}
          />
        </Suspense>
      </Card>
      <DialogTrigger
        isOpen={!!showOneTimeAPIKeyJwt}
        onOpenChange={() => setShowOneTimeAPIKeyJwt(null)}
      >
        <ModalOverlay>
          <Modal size="L">
            <OneTimeAPIKeyDialog jwt={showOneTimeAPIKeyJwt ?? ""} />
          </Modal>
        </ModalOverlay>
      </DialogTrigger>
    </div>
  );
}
